import React, { useState, useRef, useEffect } from "react";

const FAQItem = ({ question, answer }) => {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(expanded ? contentRef.current.scrollHeight : 0);
    }
  }, [expanded]);

  const toggleExpansion = () => {
    setExpanded(!expanded);
  };

  return (
    <div
      className={`w-full relative rounded-3xs box-border flex flex-row items-start justify-start p-6 gap-6 text-left text-lg text-neutrals-neutral-900 font-open-sans-ff-body transition-all duration-300 ease-in-out ${
        expanded
          ? "bg-neutrals-white-light border-[1px] border-solid border-neutrals-neutral-100 shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)]"
          : "bg-neutrals-neutral-50 hover:bg-neutrals-white-light"
      }`}
      onClick={toggleExpansion}
    >
      <div className="flex-shrink-0">
        <img
          className={`w-5 h-5 transition-all duration-300 ease-in-out transform ${
            expanded ? "rotate-45 scale-110" : "rotate-0 scale-100"
          }`}
          alt=""
          src="assets/add-circle.svg"
        />
      </div>
      <div className="flex-1 flex flex-col items-start justify-center gap-3 min-w-0">
        <div className="w-full font-semibold select-none">{question}</div>
        <div
          ref={contentRef}
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{ maxHeight: `${contentHeight}px` }}
        >
          <div className="text-base pt-2">{answer}</div>
        </div>
      </div>
    </div>
  );
};

const Fqs = ({ searchQuery = "" }) => {
  const faqs = [
    {
      question: "What is 6EL∆ ?",
      answer:
        "6EL∆ (pronounced Six-Ella) is an intelligent fintech platform that helps businesses of all sizes manage credit control, automate invoice chasing, and recover overdue payments. It acts as your Business Debt Co-Pilot, combining automation, data insight, and legal support to keep your cash flow healthy.",
    },
    {
      question: "How much does 6EL∆ cost?",
      answer:
        "6EL∆ costs  £49.99 per month for up to five user licenses for a 12 month period, making it affordable for small businesses, finance teams, and credit control departments. When a debt is successfully collected through the 6EL∆ system, a 5% collection fee is automatically deducted from the recovered amount - there are no hidden costs or setup fees.",
    },
    {
      question:
        "What are the cost implications of using 6EL∆ for debt recovery?",
      answer:
        "If your customer pays during 6EL∆'s automated Pre-Letter Before Action (Pre-LBA) stage, the system retains 5% of the amount collected as a success fee and transfers the remaining funds directly to your business bank account. You only pay when you get paid.",
    },
    {
      question: "How secure is the 6EL∆ system?",
      answer:
        "Your data security is our top priority. All information within 6EL∆ - including invoices, payment data, and client details is encrypted in transit and at rest using bank-grade AES-256 encryption. The platform is hosted on Amazon Web Services (AWS), which complies with global standards such as ISO 27001, SOC 2, and GDPR.",
    },
    {
      question: "Who can use 6EL∆?",
      answer:
        "6EL∆ is designed for a wide range of users: Businesses looking to simplify invoicing and credit control. Finance managers and credit controllers managing aged debt. Accountants, bookkeepers, and solicitors who want to support clients and streamline debt recovery.",
    },
    {
      question: "What happens when an invoice becomes overdue?",
      answer:
        "6EL∆ automatically preps the Pre-LBA (Pre-Letter Before Action) process so you only have to initiate. This sends professional, compliant reminders to your customers, giving them a fixed time to pay before escalation. Most debts are resolved at this stage without needing legal action.",
    },
    {
      question: "What if my customer still doesn't pay after reminders?",
      answer:
        "If a debtor still doesn't pay after the Pre-LBA process, 6EL∆ connects you with a specialist local law firm in your local area. The firm takes over through the 6EL∆ platform, ensuring smooth communication and access to all case information in one secure place.",
    },
    {
      question: "What kind of insights does 6EL∆ provide?",
      answer:
        "The platform includes an AI-driven dashboard that shows: • Total outstanding, paid, and overdue invoices • Payment risk levels (low, medium, high) • Collection progress and success rates • Customer payment trends over time • These insights help you make smarter decisions and act quickly to protect your cash flow.",
    },
    {
      question: "How can I contact support?",
      answer:
        "Our support team is always here to help. You can reach us by email at hello@6ela.io￼ or by using the live chat function available inside your 6EL∆ dashboard. We aim to respond to all support requests within one business day.",
    },
    {
      question: "Is 6EL∆ still in Beta?",
      answer:
        "Yes - 6EL∆ is currently in Beta release, which means we're continuing to refine and enhance the system based on user feedback. By joining now, you get early access to powerful credit control automation and the chance to shape the future of 6EL∆. We actively welcome suggestions at (enter the email address)",
    },
  ];

  // Filter FAQs based on search query
  const filteredFaqs = faqs.filter((faq) => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      faq.question.toLowerCase().includes(query) ||
      faq.answer.toLowerCase().includes(query)
    );
  });

  // Show "No results" message if search returns nothing
  if (searchQuery.trim() && filteredFaqs.length === 0) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-12 text-center">
        <div className="text-lg font-semibold text-neutrals-neutral-900 mb-2">
          No results found for "{searchQuery}"
        </div>
        <div className="text-sm text-neutrals-neutral-300">
          Try searching with different keywords
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-start justify-start gap-3">
      {filteredFaqs.map((faq, index) => (
        <FAQItem key={index} question={faq.question} answer={faq.answer} />
      ))}
      
      {/* Results count when searching */}
      {searchQuery.trim() && filteredFaqs.length > 0 && (
        <div className="w-full text-sm text-neutrals-neutral-300 text-center py-2">
          Showing {filteredFaqs.length} of {faqs.length} {filteredFaqs.length === 1 ? 'result' : 'results'}
        </div>
      )}
    </div>
  );
};

export default Fqs;